import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/Dashboard.css";
import "../styles/AdminPanel.css";
 
/* ─────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────── */
const DOCS_LIST = [
  { key: "aadhaarDoc",   label: "Aadhaar Card",          required: true  },
  { key: "panDoc",       label: "PAN Card",               required: true  },
  { key: "tenth",        label: "10th Certificate",       required: true  },
  { key: "twelfth",      label: "12th Certificate",       required: true  },
  { key: "consolidated", label: "Consolidated Marksheet", required: true  },
  { key: "experience",   label: "Experience Letters",     required: false },
  { key: "relieving",    label: "Relieving Letter",       required: false },
  { key: "passportDoc",  label: "Passport size Photo",    required: true, accept: ".jpg,.jpeg" },
];
 
const ITEMS_PER_PAGE = 10;
 
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh",
  "Uttarakhand","West Bengal","Delhi",
];
 
const AVATAR_COLORS = ["#E05C2A","#F5A623","#9B59B6","#27AE60","#3b82f6","#e74c3c","#1abc9c"];
 
const navItems = [
  { icon: "/assets/images/Employee's.png", label: "Employee Onboarding", key: "onboarding" },
  { icon: "/assets/images/Info.png",       label: "Contact Info",        key: "contact"    },
  { icon: "/assets/images/Careers.png",    label: "Careers Info",        key: "careers"    },
];
 
const VIEW_DOCS = [
  { key: "aadhaarDoc",   label: "Aadhaar Card",          dbCol: "aadhaar_doc"           },
  { key: "panDoc",       label: "PAN Card",               dbCol: "pan_doc"               },
  { key: "tenth",        label: "10th Certificates",      dbCol: "tenth_certificate"     },
  { key: "twelfth",      label: "12th Certificates",      dbCol: "twelfth_certificate"   },
  { key: "consolidated", label: "Consolidated Marksheet", dbCol: "consolidated_marksheet"},
  { key: "experience",   label: "Experience Letter",      dbCol: "experience_letters"    },
  { key: "relieving",    label: "Relieving Letter",       dbCol: "relieving_letter"      },
  { key: "passportDoc",  label: "Passport size Photo",    dbCol: "passport_photo"        },
];
 
/* ═══════════════════════════════════════════
   MODAL FIELD COMPONENTS
═══════════════════════════════════════════ */
function ModalInput({ label, required, placeholder, type = "text", value, onChange, allowOnly }) {
  const handleChange = (e) => {
    let val = e.target.value;
    if (allowOnly === "alpha")        val = val.replace(/[^A-Za-z\s]/g, "");
    if (allowOnly === "numeric")      val = val.replace(/[^0-9]/g, "");
    if (allowOnly === "alphanumeric") val = val.replace(/[^A-Za-z0-9]/g, "");
    if (allowOnly === "pan")          val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (allowOnly === "ifsc")         val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (allowOnly === "alphaspace")   val = val.replace(/[^A-Za-z0-9\s]/g, "");
    if (allowOnly === "institution")  val = val.replace(/[^A-Za-z0-9\s.,\-&]/g, "");
    if (allowOnly === "university")   val = val.replace(/[^A-Za-z0-9\s.,\-&]/g, "");
    onChange({ target: { value: val } });
  };
  return (
    <div className="modal-field">
      <label className="modal-form-label">{label}{required && <span className="modal-required">*</span>}</label>
      <input className="modal-form-input" type={type} placeholder={placeholder} value={value} onChange={handleChange} />
    </div>
  );
}
 
function ModalSelect({ label, required, options, placeholder, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="modal-field">
      <label className="modal-form-label">{label}{required && <span className="modal-required">*</span>}</label>
      <div className="modal-custom-select-wrapper" ref={ref}>
        <div className={`modal-custom-select-trigger${isOpen ? " open" : ""}${!value ? " placeholder" : ""}`} onClick={() => setIsOpen((p) => !p)}>
          <span>{value || placeholder || `Select ${label}`}</span>
          <svg className={`modal-select-chevron${isOpen ? " rotated" : ""}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 5l4 4 4-4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {isOpen && (
          <div className="modal-custom-select-dropdown">
            {options.map((o) => (
              <div key={o} className={`modal-custom-select-option${value === o ? " selected" : ""}`}
                onClick={() => { onChange({ target: { value: o } }); setIsOpen(false); }}>{o}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
 
function ModalDatePicker({ label, required, value, onChange }) {
  const toDisplay = (date) =>
    date ? `${String(date.getDate()).padStart(2, "0")} / ${String(date.getMonth() + 1).padStart(2, "0")} / ${date.getFullYear()}` : "";
  const initDate = value ? new Date(value) : null;
  const [selectedDate, setSelectedDate] = useState(initDate);
  const [inputValue,   setInputValue]   = useState(toDisplay(initDate));
  const [isOpen,       setIsOpen]       = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const handlePickerChange = (date) => {
    setSelectedDate(date); setIsOpen(false);
    const formatted = toDisplay(date);
    setInputValue(formatted);
    onChange({ target: { value: formatted } });
  };
  const handleInputChange = (e) => {
    let val = e.target.value.replace(/[^\d/\s]/g, "");
    setInputValue(val);
    const parts = val.replace(/\s/g, "").split("/");
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10), m = parseInt(parts[1], 10) - 1, y = parseInt(parts[2], 10);
      if (y > 999 && m >= 0 && m <= 11 && d >= 1 && d <= 31) {
        const parsed = new Date(y, m, d);
        if (!isNaN(parsed)) { setSelectedDate(parsed); onChange({ target: { value: val } }); return; }
      }
    }
    onChange({ target: { value: val } });
  };
  return (
    <div className="modal-field">
      <label className="modal-form-label">{label}{required && <span className="modal-required">*</span>}</label>
      <div className="modal-date-wrapper" ref={wrapperRef}>
        <input type="text" placeholder="DD / MM / YYYY" className="modal-custom-date-input" value={inputValue} onChange={handleInputChange} maxLength={14} />
        <button type="button" className="modal-calendar-toggle-btn" onClick={() => setIsOpen((p) => !p)}>
          <img src="/assets/images/cal_icon.png" alt="calendar" className="modal-calendar-icon-img" />
        </button>
        {isOpen && (
          <div className="modal-calendar-dropdown">
            <DatePicker selected={selectedDate} onChange={handlePickerChange} inline showYearDropdown scrollableYearDropdown yearDropdownItemNumber={100} />
          </div>
        )}
      </div>
    </div>
  );
}
 
function ModalFileUpload({ label, required, onChange, fileName, accept = ".pdf" }) {
  const ref = useRef();
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = accept === ".jpg,.jpeg";
    const minSize = isImage ? 100 * 1024 : 20 * 1024;
    const minLabel = isImage ? "100KB" : "20KB";
    if (file.size < minSize) { alert(`"${file.name}" is too small. Minimum file size is ${minLabel}.`); ref.current.value = ""; return; }
    if (file.size > 5 * 1024 * 1024) { alert(`"${file.name}" is too large. Maximum file size is 5MB.`); ref.current.value = ""; return; }
    onChange(e);
  };
  return (
    <div className="modal-file-upload-field">
      <label className="modal-form-label">{label}{required && <span className="modal-required">*</span>}</label>
      <div className={`modal-file-uploaded-box${fileName ? " file-selected" : ""}`} onClick={() => ref.current.click()}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke={fileName ? "#22c55e" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" />
          <polyline points="17 8 12 3 7 8" stroke={fileName ? "#22c55e" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="12" y1="3" x2="12" y2="15" stroke={fileName ? "#22c55e" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" />
        </svg>
        {fileName ? <span className="modal-file-name">{fileName}</span> : <span className="modal-file-placeholder">Choose File</span>}
        {fileName && <span className="modal-file-check">✓</span>}
      </div>
      <input ref={ref} type="file" className="modal-file-input-hidden" onChange={handleFileChange} accept={accept} />
      <span className="modal-file-size-hint">
        {accept === ".jpg,.jpeg" ? "JPEG only · 100KB – 5MB" : "PDF only · 20KB – 5MB"}
      </span>
    </div>
  );
}
 
/* ═══════════════════════════════════════════
   SHARED SMALL COMPONENTS
═══════════════════════════════════════════ */
function StatusBadge({ status }) {
  return (
    <span className={`status-badge status-${status.toLowerCase()}`}>
      <span className="status-dot"></span>{status}
    </span>
  );
}
 
function Avatar({ letter, color }) {
  return <div className="avatar" style={{ backgroundColor: color }}>{letter}</div>;
}
 
function AvatarAuto({ name }) {
  const color = name ? AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length] : AVATAR_COLORS[0];
  return <div className="avatar" style={{ backgroundColor: color }}>{name ? name.charAt(0).toUpperCase() : "?"}</div>;
}
 
function PaginationBar({ currentPage, totalPages, filtered, itemsPerPage, onPageChange, label = "records" }) {
  const pageNumbers = [];
  for (let i = 1; i <= Math.min(totalPages, 3); i++) pageNumbers.push(i);
  return (
    <div className="pagination-bar">
      <span className="pagination-info">
        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filtered)}–{Math.min(currentPage * itemsPerPage, filtered)} of {filtered} {label}
      </span>
      <div className="pagination-controls">
        <button className="page-btn arrow-btn" onClick={() => onPageChange((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
        {pageNumbers.map((p) => (
          <button key={p} className={`page-btn${currentPage === p ? " page-btn--active" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
        ))}
        {totalPages > 10 && <span className="page-ellipsis">...</span>}
        {totalPages > 10 && (
          <button className={`page-btn${currentPage === totalPages ? " page-btn--active" : ""}`} onClick={() => onPageChange(totalPages)}>{totalPages}</button>
        )}
        <button className="page-btn arrow-btn" onClick={() => onPageChange((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
      </div>
    </div>
  );
}
 
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="confirm-overlay">
      <div className="confirm-box">
        <div className="confirm-icon-wrap">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#fee2e2" />
            <path d="M12 8v4M12 16h.01" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h3 className="confirm-title">Delete Confirmation</h3>
        <p className="confirm-msg">{message}</p>
        <div className="confirm-actions">
          <button className="confirm-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="confirm-delete-btn" onClick={onConfirm}>Yes, Delete</button>
        </div>
      </div>
    </div>
  );
}
 
function SearchBar({ value, onChange }) {
  return (
    <div className="search-wrapper">
      <span className="search-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </span>
      <input type="text" className="search-input" placeholder="Search..." value={value} onChange={onChange} />
    </div>
  );
}
 
/* ═══════════════════════════════════════════
   EMPLOYEE VIEW MODAL
═══════════════════════════════════════════ */
function EmployeeViewModal({ employee, onClose }) {
  if (!employee) return null;
  const API = process.env.REACT_APP_API_URL;
  const statusClass =
    (employee.status === "Active" || employee.status === "Accepted") ? "evm-status-active" :
    employee.status === "Pending"  ? "evm-status-pending"  :
    employee.status === "Rejected" ? "evm-status-rejected" : "evm-status-pending";
 
  const Field = ({ label, value }) => (
    <div className="evm-field">
      <span className="evm-field-label">{label}</span>
      <span className="evm-field-value">{value || `—`}</span>
    </div>
  );
  const SecHeader = ({ icon, title }) => (
    <div className="evm-sec-header">
      <img src={icon} alt={title} className="evm-sec-icon" />
      <h3 className="evm-sec-title">{title}</h3>
    </div>
  );
 
  return (
    <div className="evm-panel">
      <div className="evm-header">
        <div>
          <h2 className="evm-name">{employee.name}</h2>
          <div className="evm-status-bar">
            <span className="evm-status-label">Status:</span>
            <span className={`evm-status-badge ${statusClass}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="evm-status-icon">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M8 12l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {employee.status}
            </span>
          </div>
        </div>
        <button className="evm-close" onClick={onClose}>✕</button>
      </div>
 
      <div className="evm-body">
        <div className="evm-row-2col">
          <div className="evm-section">
            <SecHeader icon="/assets/images/24_regular.png" title="Personal Information" />
            <div className="evm-card"><div className="evm-grid-2">
              <Field label="Full Name"         value={employee.name} />
              <Field label="Date of Birth"     value={employee.dob} />
              <Field label="Gender"            value={employee.gender} />
              <Field label="Marital Status"    value={employee.marital_status} />
              <Field label="Nationality"       value={employee.nationality} />
              <Field label="Personal Email ID" value={employee.email} />
              <Field label="Mobile Number"     value={employee.mobile} />
              <Field label="Alternate Number"  value={employee.alternate_number} />
            </div></div>
          </div>
          <div className="evm-section">
            <SecHeader icon="/assets/images/marker-outline.png" title="Address Details" />
            <div className="evm-card evm-card-address">
              <div className="evm-address-full">
                <span className="evm-field-label">Address</span>
                <span className="evm-field-value">{employee.address || "—"}</span>
              </div>
              <div className="evm-grid-2" style={{ marginTop: 16 }}>
                <Field label="City" value={employee.city} />
                <Field label="State" value={employee.state} />
              </div>
              <div style={{ marginTop: 16 }}><Field label="Pin Code" value={employee.pin_code} /></div>
            </div>
          </div>
        </div>
        <div className="evm-row-2col">
          <div className="evm-section">
            <SecHeader icon="/assets/images/education_1.png" title="Education Qualification" />
            <div className="evm-card"><div className="evm-grid-2">
              <Field label="Qualification"    value={employee.qualification} />
              <Field label="Institution Name" value={employee.institution_name} />
              <Field label="University/Board" value={employee.university_board} />
              <Field label="Year of Passing"  value={employee.year_of_passing} />
              <Field label="Percentage/CGPA"  value={employee.percentage_cgpa} />
            </div></div>
          </div>
          <div className="evm-section">
            <SecHeader icon="/assets/images/identity-card.png" title="Identity Details" />
            <div className="evm-card"><div className="evm-grid-2">
              <Field label="PAN Number"      value={employee.pan_number} />
              <Field label="Aadhaar Number"  value={employee.aadhaar_number} />
              <Field label="Passport Number" value={employee.passport_number} />
              <Field label="UAN"             value={employee.uan_number} />
              <Field label="ESI Number"      value={employee.esi_number} />
            </div></div>
          </div>
        </div>
        <div className="evm-row-2col">
          <div className="evm-section">
            <SecHeader icon="/assets/images/bas_bank.png" title="Bank Details" />
            <div className="evm-card"><div className="evm-grid-2">
              <Field label="Bank Name"           value={employee.bank_name} />
              <Field label="Account Holder Name" value={employee.account_holder_name} />
              <Field label="Account Number"      value={employee.account_number} />
              <Field label="IFSC Code"           value={employee.ifsc_code} />
              <Field label="Branch"              value={employee.branch_name} />
            </div></div>
          </div>
          <div className="evm-section" />
        </div>
        <div className="evm-section">
          <SecHeader icon="/assets/images/solar_documents.png" title="Documents" />
          <div className="evm-card">
            <div className="evm-docs-grid">
              {VIEW_DOCS.map((doc) => {
                const fileName = employee[doc.dbCol];
                return (
                  <div key={doc.key} className="evm-doc-card">
                    <p className="evm-doc-title">{doc.label}</p>
                    {fileName ? (
                      <div className="evm-doc-file-row">
                        <div className="evm-doc-file-info">
                          <div className="evm-doc-pdf-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <rect x="3" y="2" width="14" height="18" rx="2" fill="#dbeafe" stroke="#93c5fd" strokeWidth="1.5" />
                              <path d="M7 7h6M7 11h6M7 15h4" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div className="evm-doc-meta">
                            <span className="evm-doc-filename">{fileName}</span>
                            <span className="evm-doc-size">PDF</span>
                          </div>
                        </div>
                        <div className="evm-doc-actions">
                          <a href={`${API}/uploads/${fileName}`} target="_blank" rel="noreferrer" className="evm-doc-btn" title="View">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                            </svg>
                          </a>
                          <a href={`${API}/uploads/${fileName}`} download className="evm-doc-btn" title="Download">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: "12px", color: "#9ca3af" }}>Not uploaded</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
 
/* ═══════════════════════════════════════════
   EMPLOYEE EDIT MODAL
═══════════════════════════════════════════ */
function EmployeeEditModal({ employee, onClose, onSave }) {
  const [pendingStatus, setPendingStatus] = useState(employee?.status || "Pending");
  const locParts = employee?.location?.split(",") || [];
  const [formData, setFormData] = useState({
    fullName: employee?.name || "", dateOfBirth: employee?.dob || "",
    gender: employee?.gender || "Male", maritalStatus: employee?.marital_status || "Single",
    nationality: employee?.nationality || "Indian", personalEmail: employee?.email || "",
    mobileNumber: employee?.mobile || "", alternateNumber: employee?.alternate_number || "",
    address: employee?.address || "", city: employee?.city || locParts[0]?.trim() || "",
    state: employee?.state || locParts[1]?.trim() || "", pinCode: employee?.pin_code || "",
    qualification: employee?.qualification || "Bachelor's", institutionName: employee?.institution_name || "",
    universityBoard: employee?.university_board || "", yearOfPassing: employee?.year_of_passing || "",
    percentage: employee?.percentage_cgpa || "", panNumber: employee?.pan_number || "",
    aadhaarNumber: employee?.aadhaar_number || "", passportNumber: employee?.passport_number || "",
    uan: employee?.uan_number || "", esiNumber: employee?.esi_number || "",
    bankName: employee?.bank_name || "", accountHolderName: employee?.account_holder_name || "",
    accountNumber: employee?.account_number || "", ifscCode: employee?.ifsc_code || "",
    branch: employee?.branch_name || "",
    aadhaarDoc: employee?.aadhaar_doc || "",
    panDoc: employee?.pan_doc || "",
    tenth: employee?.tenth_certificate || "",
    twelfth: employee?.twelfth_certificate || "",
    consolidated: employee?.consolidated_marksheet || "",
    experience: employee?.experience_letters || "",
    relieving: employee?.relieving_letter || "",
    passportDoc: employee?.passport_photo || "",
  });
  const set = (k) => (e) => setFormData((prev) => ({ ...prev, [k]: e.target.value }));
  const setFile = (k) => (e) => {
    const file = e.target.files[0];
    if (file) setFormData((prev) => ({ ...prev, [k]: file.name }));
  };
 
  const handleSave = () => {
    onSave({
      ...employee,
      status:           pendingStatus,
      name:             formData.fullName,
      email:            formData.personalEmail,
      mobile:           formData.mobileNumber,
      city:             formData.city,
      state:            formData.state,
      location:         `${formData.city}, ${formData.state}`,
      avatar:           formData.fullName?.charAt(0).toUpperCase() || employee.avatar,
      dob:              formData.dateOfBirth,
      gender:           formData.gender,
      marital_status:   formData.maritalStatus,
      nationality:      formData.nationality,
      alternate_number: formData.alternateNumber,
      address:          formData.address,
      pin_code:         formData.pinCode,
      qualification:    formData.qualification,
      institution_name: formData.institutionName,
      university_board: formData.universityBoard,
      year_of_passing:  formData.yearOfPassing,
      percentage_cgpa:  formData.percentage,
      pan_number:       formData.panNumber,
      aadhaar_number:   formData.aadhaarNumber,
      passport_number:  formData.passportNumber,
      uan_number:       formData.uan,
      esi_number:       formData.esiNumber,
      bank_name:        formData.bankName,
      account_holder_name: formData.accountHolderName,
      account_number:   formData.accountNumber,
      ifsc_code:        formData.ifscCode,
      branch_name:      formData.branch,
    });
    onClose();
  };
 
  const statusClass =
    (pendingStatus === "Active" || pendingStatus === "Accepted") ? "modal-status-active" :
    pendingStatus === "Pending"  ? "modal-status-pending"  :
    pendingStatus === "Rejected" ? "modal-status-rejected" : "modal-status-pending";
 
  const SH = ({ icon, title }) => (
    <div className="modal-section-header">
      <span className="modal-section-icon">{icon}</span>
      <h3 className="modal-section-title">{title}</h3>
    </div>
  );
 
  return (
    <div className="modal-drawer">
      <div className="modal-header">
        <div className="modal-header-info">
          <h2 className="modal-emp-name">{formData.fullName || "Employee"}</h2>
        </div>
        <button className="modal-close-btn" onClick={onClose}>✕</button>
      </div>
      <div className="modal-status-bar">
        <div className="modal-status-left">
          <span className="modal-status-label">Status:</span>
          <span className={`modal-status-badge ${statusClass}`}><span className="modal-status-dot"></span>{pendingStatus}</span>
        </div>
        {pendingStatus === "Pending" && (
          <div className="modal-status-actions">
            <button className="modal-approve-btn" onClick={() => setPendingStatus("Accepted")}>Accepted</button>
            <button className="modal-reject-btn"  onClick={() => setPendingStatus("Rejected")}>Rejected</button>
          </div>
        )}
      </div>
      <div className="modal-body">
        <SH title="Personal Information" icon={<img src="/assets/images/24_regular.png" alt="" className="modal-section-icon-img" />} />
        <div className="modal-card"><div className="modal-form-grid">
          <ModalInput label="Full Name" required placeholder="Enter full name" allowOnly="alpha" value={formData.fullName} onChange={set("fullName")} />
          <ModalDatePicker label="Date of Birth" required value={formData.dateOfBirth} onChange={set("dateOfBirth")} />
          <ModalSelect label="Gender" placeholder="Select Gender" options={["Male","Female","Other"]} value={formData.gender} onChange={set("gender")} />
          <ModalSelect label="Marital Status" placeholder="Select Status" options={["Single","Married","Divorced","Widowed"]} value={formData.maritalStatus} onChange={set("maritalStatus")} />
          <ModalInput label="Nationality" placeholder="Enter Nationality" allowOnly="alpha" value={formData.nationality} onChange={set("nationality")} />
          <ModalInput label="Personal Email ID" required type="email" placeholder="Enter Email ID" value={formData.personalEmail} onChange={set("personalEmail")} />
          <ModalInput label="Mobile Number" required placeholder="Enter Mobile Number" allowOnly="numeric" value={formData.mobileNumber} onChange={set("mobileNumber")} />
          <ModalInput label="Alternate Number" placeholder="Enter Alternate Number" allowOnly="numeric" value={formData.alternateNumber} onChange={set("alternateNumber")} />
        </div></div>
 
        <SH title="Address Details" icon={<img src="/assets/images/marker-outline.png" alt="" className="modal-section-icon-img" />} />
        <div className="modal-card"><div className="modal-form-grid">
          <div className="modal-field modal-field-full">
            <label className="modal-form-label">Address<span className="modal-required">*</span></label>
            <textarea className="modal-form-textarea" placeholder="Enter your address" value={formData.address} onChange={set("address")} rows={3} />
          </div>
          <ModalInput label="City" required placeholder="Enter your City" allowOnly="alpha" value={formData.city} onChange={set("city")} />
          <ModalSelect label="State" required placeholder="Select State" options={INDIAN_STATES} value={formData.state} onChange={set("state")} />
          <ModalInput label="Pin Code" required placeholder="Enter Pin Code" allowOnly="numeric" value={formData.pinCode} onChange={set("pinCode")} />
        </div></div>
 
        <SH title="Education Qualification" icon={<img src="/assets/images/education_1.png" alt="" className="modal-section-icon-img" />} />
        <div className="modal-card"><div className="modal-form-grid">
          <ModalSelect label="Qualification" required placeholder="Select Qualification" options={["High School","Diploma","Bachelor's","Master's","PhD","Other"]} value={formData.qualification} onChange={set("qualification")} />
          <ModalInput label="Institution Name" required placeholder="Enter Institution Name" allowOnly="institution" value={formData.institutionName} onChange={set("institutionName")} />
          <ModalInput label="University/Board" placeholder="Enter University/Board" allowOnly="university" value={formData.universityBoard} onChange={set("universityBoard")} />
          <ModalInput label="Year of Passing" required placeholder="YYYY" allowOnly="numeric" value={formData.yearOfPassing} onChange={set("yearOfPassing")} />
          <ModalInput label="Percentage/CGPA" placeholder="Enter Percentage/CGPA" value={formData.percentage} onChange={set("percentage")} />
        </div></div>
 
        <SH title="Identity Details" icon={<img src="/assets/images/identity-card.png" alt="" className="modal-section-icon-img" />} />
        <div className="modal-card"><div className="modal-form-grid">
          <ModalInput label="PAN Number" required placeholder="Enter PAN Number" allowOnly="pan" value={formData.panNumber} onChange={set("panNumber")} />
          <ModalInput label="Aadhaar Number" required placeholder="12-Digit Aadhaar Number" allowOnly="numeric" value={formData.aadhaarNumber} onChange={set("aadhaarNumber")} />
          <ModalInput label="Passport Number" placeholder="Enter Passport Number" allowOnly="alphanumeric" value={formData.passportNumber} onChange={set("passportNumber")} />
          <ModalInput label="UAN" placeholder="Enter UAN" allowOnly="numeric" value={formData.uan} onChange={set("uan")} />
          <ModalInput label="ESI Number" placeholder="Enter ESI Number" allowOnly="numeric" value={formData.esiNumber} onChange={set("esiNumber")} />
        </div></div>
 
        <SH title="Bank Details" icon={<img src="/assets/images/bas_bank.png" alt="" className="modal-section-icon-img" />} />
        <div className="modal-card"><div className="modal-form-grid">
          <ModalInput label="Bank Name" required placeholder="Enter Bank Name" allowOnly="alpha" value={formData.bankName} onChange={set("bankName")} />
          <ModalInput label="Account Holder Name" required placeholder="As Per Bank Records" allowOnly="alpha" value={formData.accountHolderName} onChange={set("accountHolderName")} />
          <ModalInput label="Account Number" required placeholder="Enter Account Number" allowOnly="numeric" value={formData.accountNumber} onChange={set("accountNumber")} />
          <ModalInput label="IFSC Code" required placeholder="Enter IFSC Code" allowOnly="ifsc" value={formData.ifscCode} onChange={set("ifscCode")} />
          <ModalInput label="Branch" placeholder="Enter Branch Name" allowOnly="alphaspace" value={formData.branch} onChange={set("branch")} />
        </div></div>
 
        <SH title="Document Upload" icon={<img src="/assets/images/solar_documents.png" alt="" className="modal-section-icon-img" />} />
        <div className="modal-card">
          <div className="modal-docs-upload-grid">
            {DOCS_LIST.map((doc) => (
              <ModalFileUpload
                key={doc.key}
                label={doc.label}
                required={doc.required}
                accept={doc.accept}
                fileName={formData[doc.key]}
                onChange={setFile(doc.key)}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="modal-cancel-btn" onClick={onClose}>Cancel</button>
        <button className="modal-save-btn" onClick={handleSave}>Save</button>
      </div>
    </div>
  );
}
 
/* ═══════════════════════════════════════════
   CONTACT & CAREER MODALS
═══════════════════════════════════════════ */
function ContactViewModal({ contact, onClose }) {
  if (!contact) return null;
  return (
    <div className="ci-modal-overlay" onClick={onClose}>
      <div className="ci-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ci-modal-header">
          <h3 className="ci-modal-title">View Contact Info</h3>
          <button className="ci-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ci-modal-body">
          <div className="ci-modal-grid">
            <div className="ci-modal-field"><span className="ci-field-label">First Name</span><span className="ci-field-value">{contact.first_name}</span></div>
            <div className="ci-modal-field"><span className="ci-field-label">Last Name</span><span className="ci-field-value">{contact.last_name}</span></div>
          </div>
          <div className="ci-modal-divider" />
          <div className="ci-modal-grid">
            <div className="ci-modal-field"><span className="ci-field-label">Work Email ID</span><span className="ci-field-value">{contact.work_email}</span></div>
            <div className="ci-modal-field"><span className="ci-field-label">Phone Number</span><span className="ci-field-value">{contact.phone_number}</span></div>
          </div>
          <div className="ci-modal-divider" />
          <div className="ci-modal-grid">
            <div className="ci-modal-field"><span className="ci-field-label">Company Name</span><span className="ci-field-value">{contact.company_name}</span></div>
            <div className="ci-modal-field"><span className="ci-field-label">Location</span><span className="ci-field-value">{contact.location}</span></div>
          </div>
          <div className="ci-modal-divider" />
          <div className="ci-modal-field ci-modal-field-full"><span className="ci-field-label">Requirement</span><span className="ci-field-value">{contact.requirements}</span></div>
        </div>
      </div>
    </div>
  );
}
 
function CareerEditModal({ career, onClose, onSave }) {
  const [form, setForm] = useState({
    full_name: career?.full_name || "", email_id: career?.email_id || "",
    phone_number: career?.phone_number || "", experience: career?.experience || "",
    position_apply_for: career?.position_apply_for || "", location: career?.location || "",
  });
  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  return (
    <div className="ci-modal-overlay" onClick={onClose}>
      <div className="career-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="career-edit-modal-header"><h3 className="career-edit-modal-title">Edit Career Info</h3></div>
        <div className="career-edit-modal-body">
          {[
            { label: "Full Name",  key: "full_name",          placeholder: "Full Name" },
            { label: "Email ID",   key: "email_id",           placeholder: "Email ID" },
            { label: "Mobile",     key: "phone_number",       placeholder: "Mobile Number" },
            { label: "Experience", key: "experience",         placeholder: "e.g. Fresher, 1 Year" },
            { label: "Position",   key: "position_apply_for", placeholder: "Position applied for" },
            { label: "Location",   key: "location",           placeholder: "Location" },
          ].map(({ label, key, placeholder }) => (
            <div className="career-edit-field" key={key}>
              <label className="career-edit-label">{label}</label>
              <input className="career-edit-input" value={form[key]} onChange={set(key)} placeholder={placeholder} />
            </div>
          ))}
        </div>
        <div className="career-edit-modal-footer">
          <button className="career-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="career-save-btn" onClick={() => onSave(form)}>Save</button>
        </div>
      </div>
    </div>
  );
}
 
function ContactEditModal({ contact, onClose, onSave }) {
  const [form, setForm] = useState({
    first_name: contact?.first_name || "", last_name: contact?.last_name || "",
    phone_number: contact?.phone_number || "", work_email: contact?.work_email || "",
    company_name: contact?.company_name || "", location: contact?.location || "",
    requirements: contact?.requirements || "",
  });
  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  return (
    <div className="ci-modal-overlay" onClick={onClose}>
      <div className="career-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="career-edit-modal-header"><h3 className="career-edit-modal-title">Edit Contact Info</h3></div>
        <div className="career-edit-modal-body">
          {[
            { label: "First Name",   key: "first_name",   placeholder: "First Name" },
            { label: "Last Name",    key: "last_name",    placeholder: "Last Name" },
            { label: "Phone",        key: "phone_number", placeholder: "Phone Number" },
            { label: "Email",        key: "work_email",   placeholder: "Work Email" },
            { label: "Company",      key: "company_name", placeholder: "Company Name" },
            { label: "Location",     key: "location",     placeholder: "Location" },
            { label: "Requirements", key: "requirements", placeholder: "Requirements" },
          ].map(({ label, key, placeholder }) => (
            <div className="career-edit-field" key={key}>
              <label className="career-edit-label">{label}</label>
              <input className="career-edit-input" value={form[key]} onChange={set(key)} placeholder={placeholder} />
            </div>
          ))}
        </div>
        <div className="career-edit-modal-footer">
          <button className="career-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="career-save-btn" onClick={() => onSave(form)}>Save</button>
        </div>
      </div>
    </div>
  );
}
 
/* ═══════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
═══════════════════════════════════════════ */
const Dashboard = () => {
  const [activeNav, setActiveNav] = useState("onboarding");
  const API = process.env.REACT_APP_API_URL;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
 
  /* ── Employee Onboarding ── */
  const [employeeData,  setEmployeeData]  = useState([]);
  const [empLoading,    setEmpLoading]    = useState(true);
  const [empFetchError, setEmpFetchError] = useState("");
  const [empSearch,     setEmpSearch]     = useState("");
  const [empFilter,     setEmpFilter]     = useState("All Employee");
  const [empPage,       setEmpPage]       = useState(1);
  const [empView,       setEmpView]       = useState("table");
  const [editEmp,       setEditEmp]       = useState(null);
  const [viewEmp,       setViewEmp]       = useState(null);
 
  const fetchEmployees = async () => {
    setEmpLoading(true);
    setEmpFetchError("");
    try {
      const res  = await fetch(`${API}/onboarding`);
      const data = await res.json();
      const withMeta = data.map((emp) => {
        const rawStatus = (emp.status || "").toLowerCase();
        const status =
          rawStatus === "accepted" ? "Accepted" :
          rawStatus === "pending"  ? "Pending"  :
          rawStatus === "rejected" ? "Rejected" : "Pending";
        return {
          ...emp,
          status,
          color:  emp.name ? AVATAR_COLORS[emp.name.charCodeAt(0) % AVATAR_COLORS.length] : AVATAR_COLORS[0],
          avatar: emp.name ? emp.name.charAt(0).toUpperCase() : "?",
        };
      });
      setEmployeeData(withMeta);
    } catch (err) {
      console.error("Fetch employees error:", err);
      setEmpFetchError("Failed to load employee data.");
    } finally {
      setEmpLoading(false);
    }
  };
 
  useEffect(() => { fetchEmployees(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
 
  const deleteEmployee = (id) => {
    setConfirmDialog({
      message: "Are you sure you want to delete this employee? This action cannot be undone.",
      onConfirm: async () => {
        try {
          await fetch(`${API}/onboarding/${id}`, { method: "DELETE" });
          setEmployeeData((prev) => prev.filter((e) => e.id !== id));
        } catch (err) {
          console.error("Delete employee error:", err);
        }
        setConfirmDialog(null);
      },
    });
  };
 
  const saveEmployee = async (updated) => {
    try {
      await fetch(`${API}/onboarding/${updated.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:   updated.name,
          email:  updated.email,
          mobile: updated.mobile,
          city:   updated.city,
          state:  updated.state,
          status: updated.status,
        }),
      });
      setEmployeeData((prev) =>
        prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e))
      );
    } catch (err) {
      console.error("Save employee error:", err);
    }
    setEmpView("table");
  };
 
  /* ── Contact Info ── */
  const [contacts,         setContacts]         = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editContactData,  setEditContactData]  = useState(null);
  const [contactSearch,    setContactSearch]    = useState("");
  const [contactPage,      setContactPage]      = useState(1);
  const [viewContact,      setViewContact]      = useState(null);
 
  /* ── Careers Info ── */
  const [careers,         setCareers]         = useState([]);
  const [showModal,       setShowModal]       = useState(false);
  const [editData,        setEditData]        = useState(null);
  const [careerSearch,    setCareerSearch]    = useState("");
  const [careerPage,      setCareerPage]      = useState(1);
  const [selectedCareers, setSelectedCareers] = useState([]); // ← bulk delete state
 
  /* ── Shared UI ── */
  const [confirmDialog, setConfirmDialog] = useState(null);
 
  const handleLogout = () => {
  localStorage.removeItem("token"); // ← ADD THIS
  navigate("/admin/login");
};
 
  useEffect(() => {
    fetch(`${API}/careers`).then((r) => r.json()).then((d) => setCareers(d)).catch((e) => console.log(e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetch(`${API}/contacts`).then((r) => r.json()).then((d) => setContacts(d)).catch((e) => console.log(e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
 
  /* ── Career handlers ── */
  const deleteCareer = (id) => {
    setConfirmDialog({
      message: "Are you sure you want to delete this career entry? This action cannot be undone.",
      onConfirm: async () => {
        try { await fetch(`${API}/careers/${id}`, { method: "DELETE" }); setCareers(careers.filter((i) => i.id !== id)); }
        catch (e) { console.log(e); }
        setConfirmDialog(null);
      },
    });
  };
 
  // ── Bulk delete handler ──
  const bulkDeleteCareers = () => {
    if (selectedCareers.length === 0) return;
    setConfirmDialog({
      message: `Are you sure you want to delete ${selectedCareers.length} selected applicant(s)? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await Promise.all(selectedCareers.map(id => fetch(`${API}/careers/${id}`, { method: "DELETE" })));
          setCareers(careers.filter(c => !selectedCareers.includes(c.id)));
          setSelectedCareers([]);
        } catch (e) { console.log(e); }
        setConfirmDialog(null);
      },
    });
  };
 
  const openEditModal = (item) => { setEditData({ ...item }); setShowModal(true); };
  const saveEdit = async (formData) => {
    try {
      await fetch(`${API}/careers/${editData.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ full_name: formData.full_name, email_id: formData.email_id, phone_number: formData.phone_number, experience: formData.experience, position_apply_for: formData.position_apply_for, location: formData.location }),
      });
      setCareers(careers.map((c) => (c.id === editData.id ? { ...c, ...formData } : c)));
      setShowModal(false); setEditData(null);
    } catch (e) { console.log(e); }
  };
 
  /* ── Contact handlers ── */
  const deleteContact = (id) => {
    setConfirmDialog({
      message: "Are you sure you want to delete this contact? This action cannot be undone.",
      onConfirm: async () => {
        try { await fetch(`${API}/contacts/${id}`, { method: "DELETE" }); setContacts(contacts.filter((i) => i.id !== id)); }
        catch (e) { console.log(e); }
        setConfirmDialog(null);
      },
    });
  };
  const openEditContactModal = (item) => { setEditContactData({ ...item }); setShowContactModal(true); };
  const saveContactEdit = async (formData) => {
    try {
      await fetch(`${API}/contacts/${editContactData.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: formData.first_name, last_name: formData.last_name, phone_number: formData.phone_number, work_email: formData.work_email, company_name: formData.company_name, location: formData.location, requirements: formData.requirements }),
      });
      setContacts(contacts.map((c) => (c.id === editContactData.id ? { ...c, ...formData } : c)));
      setShowContactModal(false); setEditContactData(null);
    } catch (e) { console.log(e); }
  };
 
  /* ── Derived data ── */
  const empFilters  = ["All Employee", "Accepted", "Pending", "Rejected"];
  const empFiltered = employeeData.filter((emp) => {
    const mf = empFilter === "All Employee" || emp.status === empFilter;
    const ms = (emp.name || "").toLowerCase().includes(empSearch.toLowerCase()) ||
               (emp.empId || "").toLowerCase().includes(empSearch.toLowerCase()) ||
               (emp.email || "").toLowerCase().includes(empSearch.toLowerCase());
    return mf && ms;
  });
  const empTotalPages = Math.max(1, Math.ceil(empFiltered.length / ITEMS_PER_PAGE));
  const empPaginated  = empFiltered.slice((empPage - 1) * ITEMS_PER_PAGE, empPage * ITEMS_PER_PAGE);
  const empStats = {
    total:    employeeData.length,
    active:   employeeData.filter((e) => e.status === "Accepted").length,
    pending:  employeeData.filter((e) => e.status === "Pending").length,
    rejected: employeeData.filter((e) => e.status === "Rejected").length,
  };
 
  const filteredContacts  = contacts.filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(contactSearch.toLowerCase()) || (c.work_email || "").toLowerCase().includes(contactSearch.toLowerCase()) || (c.company_name || "").toLowerCase().includes(contactSearch.toLowerCase()) || (c.location || "").toLowerCase().includes(contactSearch.toLowerCase()));
  const contactTotalPages = Math.max(1, Math.ceil(filteredContacts.length / ITEMS_PER_PAGE));
  const contactPaginated  = filteredContacts.slice((contactPage - 1) * ITEMS_PER_PAGE, contactPage * ITEMS_PER_PAGE);
 
  const filteredCareers  = careers.filter((c) => (c.full_name || "").toLowerCase().includes(careerSearch.toLowerCase()) || (c.email_id || "").toLowerCase().includes(careerSearch.toLowerCase()) || (c.position_apply_for || "").toLowerCase().includes(careerSearch.toLowerCase()) || (c.location || "").toLowerCase().includes(careerSearch.toLowerCase()) || (c.experience || "").toLowerCase().includes(careerSearch.toLowerCase()));
  const careerTotalPages = Math.max(1, Math.ceil(filteredCareers.length / ITEMS_PER_PAGE));
  const careerPaginated  = filteredCareers.slice((careerPage - 1) * ITEMS_PER_PAGE, careerPage * ITEMS_PER_PAGE);
 
  const pageTitle = { onboarding: "Employee Onboarding", contact: "Contact Info", careers: "Career Information" }[activeNav];
 
  /* ═══════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════ */
  return (
    <div className="app-container" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, overflow: 'hidden' }}>
      {/* Backdrop */}
      <div
        className={`sidebar-backdrop${sidebarOpen ? " sidebar-backdrop--open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />
 
      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
        <div className="sidebar-logo">
          <span className="logo-icon">⚡</span>
          <span className="logo-text">Admin Panel</span>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              className={`nav-item${activeNav === item.key ? " nav-item--active" : ""}`}
              onClick={() => { setActiveNav(item.key); setSidebarOpen(false); }}
            >
              <span className="nav-icon"><img src={item.icon} alt={item.label} /></span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
 
      {/* Main */}
      <main className="main-content" style={{ overflowY: 'auto' }}>
        <div className="page-header">
          <button className="hamburger-btn" onClick={() => setSidebarOpen((p) => !p)} aria-label="Open menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6"  x2="21" y2="6"  />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div style={{ flex: 1 }}>
            <h1 className="page-title">{pageTitle}</h1>
            <p className="page-subtitle page-subtitle--desktop">Manage and track all records</p>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <img src="/assets/images/symbols_logout.png" alt="Logout" className="logout-icon" />
            <span className="logout-text">Logout</span>
          </button>
        </div>
 
        <div className="content-body">
 
          {/* ════ EMPLOYEE ONBOARDING ════ */}
          {activeNav === "onboarding" && (
            <>
              {empView === "table" && (
                <>
                  {empLoading && <p style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>Loading employees…</p>}
                  {empFetchError && <p style={{ textAlign: "center", padding: "20px", color: "#dc2626" }}>{empFetchError}</p>}
 
                  {!empLoading && !empFetchError && (
                    <>
                      <div className="stats-grid">
                        <div className="stat-card">
                          <div className="stat-icon stat-icon-blue"><img src="/assets/images/people_24.png" alt="Total" /></div>
                          <div className="stat-info"><span className="stat-label">Total Employees</span><span className="stat-value">{empStats.total}</span></div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon stat-icon-green"><img src="/assets/images/ring_round.png" alt="Active" /></div>
                          <div className="stat-info"><span className="stat-label">Accepted</span><span className="stat-value">{empStats.active}</span></div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon stat-icon-amber"><img src="/assets/images/lucide_clock.png" alt="Pending" /></div>
                          <div className="stat-info"><span className="stat-label">Pending</span><span className="stat-value">{empStats.pending}</span></div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon stat-icon-red"><img src="/assets/images/cancel_outline.png" alt="Rejected" /></div>
                          <div className="stat-info"><span className="stat-label">Rejected</span><span className="stat-value">{empStats.rejected}</span></div>
                        </div>
                      </div>
 
                      <div className="table-section">
                        <div className="table-header">
                          <div className="table-title-wrap">
                            <h2 className="table-title">Employee Records</h2>
                            <p className="table-subtitle">Showing {empFiltered.length} employees</p>
                          </div>
                          <div className="table-controls">
                            <SearchBar value={empSearch} onChange={(e) => { setEmpSearch(e.target.value); setEmpPage(1); }} />
                            <div className="filter-tabs">
                              {empFilters.map((f) => (
                                <button key={f} className={`filter-tab${empFilter === f ? " filter-tab--active" : ""}`}
                                  onClick={() => { setEmpFilter(f); setEmpPage(1); }}>{f}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="table_1-wrapper">
                          <table className="emp-table">
                            <thead><tr>
                              <th>S.No</th><th>Employee Name</th>
                              <th>Employee Email</th><th>Mobile</th><th>Location</th>
                              <th>Status</th><th>Action</th>
                            </tr></thead>
                            <tbody>
                              {empPaginated.map((emp, i) => (
                                <tr key={emp.id}>
                                  <td className="td-sno">{(empPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                                  <td><div className="emp-name-cell"><Avatar letter={emp.avatar} color={emp.color} /><span className="emp-name">{emp.name}</span></div></td>
                                  <td className="td-email">{emp.email}</td>
                                  <td className="td-mobile">{emp.mobile}</td>
                                  <td className="td-location">{emp.location}</td>
                                  <td><StatusBadge status={emp.status} /></td>
                                  <td>
                                    <div className="action-buttons">
                                      <button className="action-btn edit-btn" title="Edit"
                                        onClick={() => { setEditEmp(emp); setEmpView("modal"); }}>
                                        <img src="/assets/images/edits.png" alt="Edit" />
                                      </button>
                                      <button className="action-btn delete-btn" title="Delete"
                                        onClick={() => deleteEmployee(emp.id)}>
                                        <img src="/assets/images/deletes.png" alt="Delete" />
                                      </button>
                                      <button className="action-btn view-btn_1" title="View"
                                        onClick={() => { setViewEmp(emp); setEmpView("view"); }}>
                                        <img src="/assets/images/views.png" alt="View" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <PaginationBar currentPage={empPage} totalPages={empTotalPages} filtered={empFiltered.length}
                          itemsPerPage={ITEMS_PER_PAGE} onPageChange={setEmpPage} label="employees" />
                      </div>
                    </>
                  )}
                </>
              )}
 
              {empView === "modal" && (
                <EmployeeEditModal employee={editEmp}
                  onClose={() => { setEditEmp(null); setEmpView("table"); }}
                  onSave={saveEmployee} />
              )}
              {empView === "view" && (
                <EmployeeViewModal employee={viewEmp} onClose={() => { setViewEmp(null); setEmpView("table"); }} />
              )}
            </>
          )}
 
          {/* ════ CONTACT INFO ════ */}
          {activeNav === "contact" && (
            <div className="table-section">
              <div className="table-header">
                <div className="table-title-wrap">
                  <h2 className="table-title">Contact Records</h2>
                  <p className="table-subtitle">Showing {filteredContacts.length} contacts</p>
                </div>
                <div className="table-controls">
                  <SearchBar value={contactSearch} onChange={(e) => { setContactSearch(e.target.value); setContactPage(1); }} />
                </div>
              </div>
              <div className="table_1-wrapper">
                <table className="emp-table">
                  <thead><tr>
                    <th>S.No</th><th>First Name</th><th>Last Name</th>
                    <th>Work Email</th><th>Phone No</th><th>Company Name</th>
                    <th>Location</th><th>Requirements</th><th>Action</th>
                  </tr></thead>
                  <tbody>
                    {contactPaginated.map((item, index) => (
                      <tr key={item.id}>
                        <td className="td-sno">{(contactPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td><div className="emp-name-cell"><AvatarAuto name={item.first_name} /><span className="emp-name">{item.first_name}</span></div></td>
                        <td>{item.last_name}</td>
                        <td className="td-email">{item.work_email}</td>
                        <td className="td-mobile">{item.phone_number}</td>
                        <td>{item.company_name}</td>
                        <td className="td-location">{item.location}</td>
                        <td className="td-requirement">{item.requirements}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="action-btn edit-btn" title="Edit" onClick={() => openEditContactModal(item)}><img src="/assets/images/edits.png" alt="Edit" /></button>
                            <button className="action-btn delete-btn" title="Delete" onClick={() => deleteContact(item.id)}><img src="/assets/images/deletes.png" alt="Delete" /></button>
                            <button className="action-btn view-btn_1" title="View" onClick={() => setViewContact(item)}><img src="/assets/images/views.png" alt="View" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar currentPage={contactPage} totalPages={contactTotalPages} filtered={filteredContacts.length}
                itemsPerPage={ITEMS_PER_PAGE} onPageChange={setContactPage} label="contacts" />
            </div>
          )}
 
          {/* ════ CAREERS INFO ════ */}
          {activeNav === "careers" && (
            <div className="table-section">
              <div className="table-header">
                <div className="table-title-wrap">
                  <h2 className="table-title">Career Applications</h2>
                  <p className="table-subtitle">Showing {filteredCareers.length} applicants</p>
                </div>
                <div className="table-controls">
                  {/* ── Bulk Delete Button — appears only when items are selected ── */}
                  {selectedCareers.length > 0 && (
                    <button
                      onClick={bulkDeleteCareers}
                      style={{
                        background: "#dc2626", color: "#fff", border: "none",
                        borderRadius: "8px", padding: "8px 16px", cursor: "pointer",
                        fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap",
                        marginTop:"49px",marginRight: "109px",
                      }}
                    >
                      Bulk Delete ({selectedCareers.length})
                    </button>
                  )}
                  <SearchBar value={careerSearch} onChange={(e) => { setCareerSearch(e.target.value); setCareerPage(1); }} />
                </div>
              </div>
              <div className="table_1-wrapper">
                <table className="emp-table">
                  <thead><tr>
                    {/* ── Select All Checkbox ── */}
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedCareers.length === careerPaginated.length && careerPaginated.length > 0}
                        onChange={(e) => setSelectedCareers(e.target.checked ? careerPaginated.map(c => c.id) : [])}
                      />
                    </th>
                    <th>S.No</th><th>Full Name</th><th>Email ID</th><th>Phone No</th>
                    <th>Experience</th><th>Position</th><th>Location</th>
                    <th>Resume</th><th>Created</th><th>Action</th>
                  </tr></thead>
                  <tbody>
                    {careerPaginated.map((item, index) => (
                      <tr key={item.id}>
                        {/* ── Row Checkbox ── */}
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedCareers.includes(item.id)}
                            onChange={(e) => setSelectedCareers(prev =>
                              e.target.checked ? [...prev, item.id] : prev.filter(id => id !== item.id)
                            )}
                          />
                        </td>
                        <td className="td-sno">{(careerPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td><div className="emp-name-cell"><AvatarAuto name={item.full_name} /><span className="emp-name">{item.full_name}</span></div></td>
                        <td className="td-email">{item.email_id}</td>
                        <td className="td-mobile">{item.phone_number}</td>
                        <td>{item.experience}</td>
                        <td>{item.position_apply_for}</td>
                        <td className="td-location">{item.location}</td>
                        <td>
                          <a href={`${API}/uploads/${item.resume_pdf}`} className="career-resume-link" target="_blank" rel="noreferrer">
                            <span className="career-resume-dot">●</span> View
                          </a>
                        </td>
                        <td className="td-created">{item.created_at}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="action-btn edit-btn" title="Edit" onClick={() => openEditModal(item)}><img src="/assets/images/edits.png" alt="Edit" /></button>
                            <button className="action-btn delete-btn" title="Delete" onClick={() => deleteCareer(item.id)}><img src="/assets/images/deletes.png" alt="Delete" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar currentPage={careerPage} totalPages={careerTotalPages} filtered={filteredCareers.length}
                itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCareerPage} label="applicants" />
            </div>
          )}
 
        </div>
      </main>
 
      {/* Modals */}
      {showModal && editData && (
        <CareerEditModal career={editData} onClose={() => { setShowModal(false); setEditData(null); }} onSave={saveEdit} />
      )}
      {showContactModal && editContactData && (
        <ContactEditModal contact={editContactData} onClose={() => { setShowContactModal(false); setEditContactData(null); }} onSave={saveContactEdit} />
      )}
      {viewContact && <ContactViewModal contact={viewContact} onClose={() => setViewContact(null)} />}
 
      {/* Confirm Dialog */}
      {confirmDialog && (
        <ConfirmDialog message={confirmDialog.message} onConfirm={confirmDialog.onConfirm} onCancel={() => setConfirmDialog(null)} />
      )}
    </div>
  );
};
 
export default Dashboard;